import dayjs from 'dayjs';
import type { LocationQuery, LocationQueryRaw } from 'vue-router';

/** True when path is /board/{board_id} followed by nothing, a segment, a query or a hash. */
export function isPathOfBoard(board_id: number | string | null | undefined, path: string | null | undefined) {
   const id = board_id?.toString();
   if (!id || typeof path !== 'string') return false;

   const prefix = `/board/${id}`;
   return path.startsWith(prefix) && ['', '/', '?', '#'].includes(path.charAt(prefix.length));
}

export function setBoardUrlInLocalStorage({ board_id, path }: { board_id: number | string; path: string }) {
   if (!isPathOfBoard(board_id, path)) return;

   try {
      localStorage.setItem(`board-last-url-${board_id}`, path);
   } catch {
      // localStorage unavailable or full, skip saving
   }
}

export function getBoardUrlFromLocalStorage(board_id: number | string) {
   try {
      return localStorage.getItem(`board-last-url-${board_id}`);
   } catch {
      return null;
   }
}

const boardExportReturnPaths = new Map<string, string>();

export function isValidBoardExportReturnPath(path: string) {
   return (
      !!path &&
      path.split('/')?.[1] === 'board' &&
      !path.includes('/export') &&
      !path.includes('/settings') &&
      !path.includes('/import') &&
      !path.includes('wizard')
   );
}

/** Store where to return when closing BoardExport. Ignores modal / invalid paths so remounts keep the original. */
export function setBoardExportReturnPath(boardId: string | string[] | undefined, path: string | null | undefined) {
   const id = boardId?.toString();
   if (!id || !path || !isValidBoardExportReturnPath(path)) return;
   boardExportReturnPaths.set(id, path);
}

export function clearBoardExportReturnPath(boardId: string | string[] | undefined) {
   const id = boardId?.toString();
   if (!id) return;
   boardExportReturnPaths.delete(id);
}

export function takeBoardExportReturnPath(boardId: string | string[] | undefined) {
   const id = boardId?.toString();
   if (!id) return null;
   const path = boardExportReturnPaths.get(id) ?? null;
   boardExportReturnPaths.delete(id);
   return path;
}

export function getDefaultBoardDateQuery() {
   return {
      from: dayjs().startOf('week').format('YYYY-MM-DD'),
      to: dayjs().endOf('week').format('YYYY-MM-DD'),
   };
}

export function withDefaultBoardDateQuery(query: LocationQuery | LocationQueryRaw = {}): LocationQueryRaw {
   const defaults = getDefaultBoardDateQuery();

   return {
      ...query,
      from: query.from?.toString() || defaults.from,
      to: query.to?.toString() || defaults.to,
   };
}

export function setUserPlanningViewInLocalStorage(user_id: number | string, type: string, planning_view: string, board_id?: number | string) {
   const key = board_id ? `${user_id}-planning-view-${type}-${board_id}` : `${user_id}-planning-view-${type}`;
   localStorage.setItem(key, planning_view);
}

export function getUserPlanningViewFromLocalStorage(user_id: number | string, type: string, board_id?: number | string): string | null {
   const key = board_id ? `${user_id}-planning-view-${type}-${board_id}` : `${user_id}-planning-view-${type}`;
   return localStorage.getItem(key);
}
